import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { API, errMsg, dashboardFor } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FIELD = "w-full border border-stone-300 bg-white rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", mobile: "", city: "", state: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const go = (user) => nav(dashboardFor(user.role));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        const idf = f.email.trim();
        const isMobile = /^[0-9]{10}$/.test(idf);
        const { data } = isMobile
          ? await API.post("/auth/rider-login", { mobile: idf, password: f.password })
          : await API.post("/auth/login", { email: idf, password: f.password });
        setUser(data.user); toast.success(`Welcome back, ${data.user.name}`); go(data.user);
      } else {
        await API.post("/auth/forgot-password", { email: f.email });
        toast.success("If the email exists, a reset link has been generated.");
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1716504628105-bd76d91e85f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Suvikka" className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1739429942851-9083ee185d3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"; }} />
        <div className="absolute inset-0 bg-stone-950/40" />
        <div className="absolute bottom-10 left-10 text-white">
          <p className="font-display text-3xl font-semibold">SUVIKKA</p>
          <p className="text-sm text-stone-200 mt-1">Everything She Loves. Under One Roof.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="flex border-b border-stone-200 mb-8" data-testid="auth-tabs">
            {[["login", "Login"], ["forgot", "Forgot Password"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`auth-tab-${k}`}
                className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#6A1B29] text-[#6A1B29]" : "border-transparent text-stone-500"}`}>
                {l}
              </button>
            ))}
          </div>
          <h1 className="font-display text-3xl font-semibold text-stone-900">
            {tab === "login" ? "Welcome Back" : "Reset Password"}
          </h1>
          <p className="text-sm text-stone-500 mt-1 mb-6">
            {tab === "login" ? "Access your Suvikka dashboard." : "We'll generate a password reset link for your email."}
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            <input required placeholder={tab === "login" ? "Email or Rider Mobile" : "Email"} value={f.email} onChange={set("email")} className={FIELD} data-testid="auth-email" />
            {tab !== "forgot" && <input required type="password" minLength={6} placeholder="Password" value={f.password} onChange={set("password")} className={FIELD} data-testid="auth-password" />}
            <button disabled={busy} data-testid="auth-submit-button"
              className="w-full bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors">
              {busy ? "Please wait…" : tab === "login" ? "LOGIN" : "SEND RESET LINK"}
            </button>
          </form>
          <p className="mt-4 text-sm text-stone-500">New to Suvikka? <Link to="/signup" className="text-[#6A1B29] font-semibold" data-testid="auth-signup-link">Create your account</Link></p>
          <div className="mt-6 text-sm text-stone-500 space-y-2">
            <p>Want to open an outlet? <Link to="/open-outlet" className="text-[#6A1B29] font-medium" data-testid="auth-outlet-link">Submit an outlet enquiry</Link></p>
            <p>Want to earn by referring? <Link to="/affiliate" className="text-[#6A1B29] font-medium" data-testid="auth-affiliate-link">Join the affiliate program</Link></p>
          </div>
          <div className="mt-6 pt-5 border-t border-stone-100 flex flex-wrap gap-x-5 gap-y-2 text-xs text-stone-400" data-testid="role-login-links">
            <Link to="/signup" className="text-[#6A1B29] font-semibold" data-testid="link-common-signup">Create account →</Link>
            <Link to="/outlet-partner/login" className="hover:text-[#6A1B29] font-medium" data-testid="link-outlet-login">Outlet Partner Login</Link>
            <Link to="/rider/login" className="hover:text-[#6A1B29] font-medium" data-testid="link-rider-login">Rider Login</Link>
            <Link to="/admin/login" className="hover:text-[#6A1B29] font-medium" data-testid="link-admin-login">Admin Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
